import { Model } from "@vuex-orm/core";
import shortid from "shortid"
import _ from "lodash"
import moment from 'moment'

import User from "./User"
import STATE_Request from "./STATE_Request"
import RequestOrder from "./RequestOrder"
import RequestContact from './RequestContact'
import RequestAPI from "../../api/requests";
import ProductsAPI from "../../api/products";
import ServicesAPI from "../../api/services";
import PhoneLine from "./PhoneLine";
import store from '../store';
import Product from "./Product";
import PaymentMethod from "./PaymentMethod";

export default class Request extends Model {
    static entity = "requests";
    static fields() {
        return {
            id: this.attr("tmp/" + shortid.generate()),
            code: this.attr(null),
            addons: this.attr(null),
            zepplinId: this.attr(null),
            companyId: this.attr(null),
            deliveryCompanyId: this.attr(null),
            STATE_request: this.hasOne(STATE_Request, 'requestId'),
            responsibleUserId: this.attr(null),
            responsibleUser: this.belongsTo(User, 'responsibleUserId'),
            requestContactId: this.attr(null),
            requestContact: this.belongsTo(RequestContact, 'requestContactId'),
            requestOrderId: this.attr(null),
            requestOrder: this.belongsTo(RequestOrder, 'requestOrderId'),
            deliveryDate: this.attr(null),
            isScheduled: this.attr(null),
            phoneLineId: this.attr(null),
            phoneLine: this.belongsTo(PhoneLine, 'phoneLineId'),
            obs: this.attr(null),
            deliveredBy: this.attr(null),
            deliveredDate: this.attr(null),
            deliveryTax: this.attr(0),
            finishedBy: this.attr(null),
            dateUpdated: this.attr(null),
            dateCreated: this.attr(moment().utc().toISOString()),
            dateRemoved: this.attr(null),
            status: this.attr('sent'), // pending, in-provision, sent, finished, in-displacement, canceled, removed
        }
    }

    static beforeCreate(model) {
        if(!model.companyId) model.companyId = store.state.auth.user.defaultCompanyId
    }

    static async load(ctx,requestId = null){
        if(ctx.utils.isPersistedId(requestId)){
            ctx.$socket.emit('presence-chat-room', 'oie')
            try {
                const { data } = await RequestAPI.getOne(requestId)
                return Request._loadPersisted(ctx,data)
            } catch(err) {
                throw new Error(err)
            }
        }
        return Request._loadDraft(ctx)
    }

    static async _loadDraft(ctx){
        const requestTmpId = `tmp/${shortid.generate()}`
        const requestContactTmpId = `tmp/${shortid.generate()}`
        const requestOrderTmpId = `tmp/${shortid.generate()}`
        const contactTmpId = `tmp/${shortid.generate()}`
        const contactPhoneTmpId = `tmp/${shortid.generate()}`
        const contactAddressTmpId = `tmp/${shortid.generate()}`
        const addressTmpId = `tmp/${shortid.generate()}`
        const requestOrderProductTmpId = `tmp/${shortid.generate()}`
        const requestOrderServiceTmpId = `tmp/${shortid.generate()}`
        const requestOrderPaymentTmpId = `tmp/${shortid.generate()}`

        let phoneLineDefaultId = null
        const phoneLines = PhoneLine.query().where('status', 'active').get()

        phoneLines.forEach(phoneLine => {
            if(phoneLine.isDefault)
                phoneLineDefaultId = phoneLine.id
        })

        // request
        ctx.VXORM.Request.insert({
            data: {
                id: requestTmpId,
                responsibleUserId: ctx.$store.state['auth']['user']['id'],
                requestContactId: requestContactTmpId,
                requestOrderId: requestOrderTmpId,
                dateCreated: moment().utc().toISOString(),
                phoneLineId: phoneLineDefaultId
            }
        })
        // request_contact
        ctx.VXORM.RequestContact.insert({
            data: {
                id: requestContactTmpId,
                contactId: contactTmpId,
                contactAddressId: contactAddressTmpId,
                contactPhoneId: contactPhoneTmpId
            }
        })
        // contact
        ctx.VXORM.Contact.insert({
            data: {
                id: contactTmpId
            }
        })
        // contact
        ctx.VXORM.ContactPhone.insert({
            data: {
                id: contactPhoneTmpId,
                contactId: contactTmpId,
                isDefault: true
            }
        })
        // contact
        ctx.VXORM.ContactAddress.insert({
            data: {
                id: contactAddressTmpId,
                contactId: contactTmpId,
                addressId: addressTmpId,
                isDefault: true
            }
        })
        // address
        ctx.VXORM.Address.insert({
            data: {
                id: addressTmpId
            }
        })
        // request_order
        ctx.VXORM.RequestOrder.insert({
            data: {
                id: requestOrderTmpId
            }
        })
        const defaultProduct = await ProductsAPI.getOne(undefined, { getIsDefault: true })
            .then(res => res.data)
        ctx.VXORM.RequestOrderProduct.insert({
            data: {
                id: requestOrderProductTmpId,
                requestOrderId: requestOrderTmpId,
                productId: defaultProduct ? defaultProduct.id : null,
                unitPrice: defaultProduct ? defaultProduct.price : 0,
                product: defaultProduct,
                _isHit: true
            }
        })
        const defaultService = await ServicesAPI.getOne(undefined, { getIsDefault: true })
            .then(res => res.data)
        ctx.VXORM.RequestOrderService.insert({
            data: {
                id: requestOrderServiceTmpId,
                requestOrderId: requestOrderTmpId,
                serviceId: defaultService ? defaultService.id : null,
                price: defaultService ? defaultService.price : 0,
                service: defaultService,
                _isHit: true
            }
        })
        ctx.VXORM.RequestOrderPayment.insert({
            data: {
                id: requestOrderPaymentTmpId,
                requestOrderId: requestOrderTmpId
            }
        })

        return requestTmpId
    }

    static _loadPersisted(ctx,request){
        if(!request.requestContactId) {
            let requestContactTmpId = `tmp/${shortid.generate()}`, contactTmpId = `tmp/${shortid.generate()}`,
            addressTmpId = `tmp/${shortid.generate()}`

            _.assign(request, {
                requestContactId: requestContactTmpId,
                requestContact: {
                    id: requestContactTmpId,
                    contactId: contactTmpId,
                    contact: {
                        id: contactTmpId,
                        contactPhones: [{ id: `tmp/${shortid.generate()}`, contactId: contactTmpId, isDefault: true }],
                        contactAddresses: [{
                            id: `tmp/${shortid.generate()}`,
                            isDefault: true,
                            contactId: contactTmpId,
                            address: { id: addressTmpId },
                            addressId: addressTmpId
                        }]
                    }
                }
            })
        }


        if(request.companyId != ctx.company.id && _.get(request, 'requestOrder.requestOrderPayments')) {
            request.requestOrder.requestOrderPayments.forEach(requestOrderPayment => {
                Object.assign(requestOrderPayment.paymentMethod, {
                    status: 'inactive'
                })
                PaymentMethod.insert({ data: requestOrderPayment.paymentMethod })
            })
        }

        if(!request.requestOrder.requestOrderServices || !request.requestOrder.requestOrderServices.length) {
            let requestOrderId = request.requestOrderId

            _.assign(request.requestOrder, {
                requestOrderServices: [{
                    id: `tmp/${shortid.generate()}`,
                    requestOrderId,
                    serviceId: `tmp/${shortid.generate()}`
                }]
            })
        }

        if(!request.requestOrder.requestOrderProducts || !request.requestOrder.requestOrderProducts.length) {
            let requestOrderId = request.requestOrderId

            _.assign(request.requestOrder, {
                requestOrderProducts: [{
                    id: `tmp/${shortid.generate()}`,
                    requestOrderId,
                    productId: `tmp/${shortid.generate()}`
                }]
            })
        }

        ctx.VXORM.Request.insert({
            data: request
        })

        return request.id
    }

    static updateModelValue(modelVXORM, fieldName, modelId, valueToUpdate) {
        modelVXORM.update({
            where: modelId,
            data: { [fieldName]: valueToUpdate }
        }).then(res => res)
    }

    deleteAllInRequest() {
        const vm = this
        if(_.result(vm, 'requestOrder'))
            vm.requestOrder.deleteAllInRequestOrder()
        if(_.result(vm, 'requestContact'))
            vm.requestContact.deleteAllInRequestContact()
        vm.$delete({ where: vm.id })
    }
}
